// CAMINHO: src/components/cases/EditMovementModal.tsx
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from 'lucide-react';
import { Movimentacao, ProcessData } from '@/types/datajud';
import { format, parseISO } from 'date-fns';

interface EditMovementModalProps {
  caseId: string;
  currentMovement: Movimentacao;
  movementIndex: number;
  currentDatajudJson: ProcessData;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: (updatedMovement: Movimentacao) => void; // Retorna o andamento editado
}

export const EditMovementModal = ({ caseId, currentMovement, movementIndex, currentDatajudJson, isOpen, onOpenChange, onSuccess }: EditMovementModalProps) => {
  const [nome, setNome] = useState(currentMovement.nome || '');
  const [complemento, setComplemento] = useState(currentMovement.complemento || '');
  const [dataHora, setDataHora] = useState(
    currentMovement.dataHora ? format(parseISO(currentMovement.dataHora), "yyyy-MM-dd'T'HH:mm") : new Date().toISOString().slice(0, 16)
  );
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Atualiza o estado local se o andamento atual mudar (ex: ao abrir o modal para outro andamento)
    setNome(currentMovement.nome || '');
    setComplemento(currentMovement.complemento || '');
    setDataHora(
      currentMovement.dataHora ? format(parseISO(currentMovement.dataHora), "yyyy-MM-dd'T'HH:mm") : new Date().toISOString().slice(0, 16)
    );
  }, [currentMovement]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome || !dataHora) {
      toast({ title: "Campos obrigatórios", description: "Por favor, preencha o nome e a data/hora do andamento.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const updatedMovement: Movimentacao = {
        ...currentMovement,
        nome,
        complemento,
        dataHora: new Date(dataHora).toISOString(),
      };

      onSuccess(updatedMovement); // Notifica o pai com o andamento editado

      toast({ title: "Sucesso!", description: "Andamento atualizado." });
      onOpenChange(false); // Fecha o modal

    } catch (err: any) {
      toast({ title: "Erro ao salvar andamento", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Andamento</DialogTitle>
          <DialogDescription>
            Ajuste os detalhes deste andamento processual.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="nome" className="text-right">Nome</Label>
              <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dataHora" className="text-right">Data/Hora</Label>
              <Input id="dataHora" type="datetime-local" value={dataHora} onChange={(e) => setDataHora(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="complemento" className="text-right">Complemento</Label>
              <Textarea id="complemento" value={complemento} onChange={(e) => setComplemento(e.target.value)} className="col-span-3" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 
              Salvar Alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
